import { FileActionUpload, FileActionButton } from "../file-action/file-action-controls";
import { Suspense, useState, useRef, useEffect } from 'react';
import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';
import OrderListDataTable from './new-order-list-datatable';
import OrderListSearch from './new-order-list-search';
import orderServices from '../../services/orders/orderservice';
import { OrderModel } from '../../models/orders/order-list-model';
import { orderExport, detailExport, invoiceExport, invoiceDetailExport } from './new-order-list-export';
import AppLoading from '../app-loading/app-loading';
import { DataTableStateEvent } from "primereact/datatable";
import { Toast } from 'primereact/toast';
import { FileUpload } from 'primereact/fileupload';
import * as XLSX from 'xlsx';
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { getOrderListDisplayValue, getOrderReviewTotals, getPendingProductCount } from './order-review-calculations';

const OrderList = () => {
    const { t } = useTranslation()
    const toast = useRef<Toast>(null);
    const [orderList, setOrderList] = useState<OrderModel[]>([]);
    const [loading, setLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [orderIDs, setOrderId] = useState<number[]>([]);
    const [infoMessage, setInfoMessage] = useState('');
    const [exportLoading, setExportLoading] = useState({
        orders: false,
        orderDetail: false,
        invoice: false,
        invoiceDetail: false
    });
    const fileUploadRef = useRef<FileUpload>(null);
    const [importLoading, setImportLoading] = useState(false);

    const currentFilters = useRef<{
        startDate?: Date;
        endDate?: Date;
        orderID?: number;
        invoiceNo?: number;
        userID?: number;
        orderType?: number;
        pageNumber?: number;
        pageSize?: number;
    }>({});

    const [orderIds, setOrderIds] = useState<any>();
    const [visible, setVisible] = useState(false);
    const [paymentOrderId, setPaymentOrderId] = useState('');

    const getNumber = (value: unknown) => {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : 0;
    };

    const withPendingProductCounts = (orders: any[]) => {
        const pendingProductsByOrderId = new Map<number, Set<string>>();

        orders.forEach((order) => {
            const orderId = getNumber(order.orderID);
            if (!orderId) return;

            const orderQty = getNumber(order.orderQty ?? order.quantity);
            const invoicedQty = getNumber(order.invoicedQuantity);
            const pendingQty = getNumber(order.pendingQuantity);
            const hasProductLevelFields =
                order.productID ||
                order.productId ||
                order.productName ||
                order.orderDetailID ||
                order.orderQty !== undefined ||
                order.invoicedQuantity !== undefined;

            if (!hasProductLevelFields) return;

            const isPendingProduct = pendingQty > 0 || (orderQty > 0 && orderQty - invoicedQty > 0);
            if (isPendingProduct) {
                const productKey = String(order.orderDetailID ?? order.productID ?? order.productId ?? order.productName ?? orderId);
                const pendingProducts = pendingProductsByOrderId.get(orderId) ?? new Set<string>();
                pendingProducts.add(productKey);
                pendingProductsByOrderId.set(orderId, pendingProducts);
            }
        });

        return orders.map((order) => ({
            ...order,
            displayPendingProductCount: pendingProductsByOrderId.get(getNumber(order.orderID))?.size,
        }));
    };

    const FeatchOrderIds = async () => {
        await orderServices.GetPullPaymentStatusLogs().then((orderIdList: any[]) => {
            setOrderIds(orderIdList);
        });
    }

    useEffect(() => {
        FeatchOrderIds();
    }, []);

    const enrichOrderDisplayValues = async (orders: OrderModel[]) => {
        const enrichedOrders = await Promise.all(
            orders.map(async (order: any) => {
                try {
                    const details = await orderServices.GetOrderByOrderId(order.orderID);
                    if (!details) {
                        return order;
                    }

                    const totals = getOrderReviewTotals(details, { includeNegativeAmounts: true });
                    const displayOrderValue = totals.totalBeforeReward > 0
                        ? totals.totalBeforeReward
                        : getOrderListDisplayValue(details);
                    const displayPendingProductCount = getPendingProductCount({
                        ...details,
                        pendingQuantity: order.pendingQuantity,
                    });

                    return {
                        ...order,
                        displayOrderValue,
                        displayPendingProductCount,
                    };
                } catch {
                    return order;
                }
            })
        );

        return enrichedOrders;
    };

    const refreshOrderList = async () => {
        setLoading(false);
        try {
            const requestBody = {
                ...currentFilters.current,
                pageNumber: pageNumber,
                pageSize: pageSize
            };

            let response;

            if (currentFilters.current.orderType && [3, 4, 5].includes(currentFilters.current.orderType)) {
                response = await orderServices.orderInvoicedDetails(requestBody);
            } else {
                response = await orderServices.orderOrderdDetails(requestBody);
            }

            const sortedData = withPendingProductCounts([...(response.invoices || [])]).sort((a, b) => b.orderID - a.orderID);
            const enrichedData = await enrichOrderDisplayValues(sortedData);
            setOrderList(enrichedData);
            const validOrderIds = enrichedData.map(order => order.orderID).filter(id => id);
            setOrderId(validOrderIds);
            setTotalRecords(response.totalRecords || 0);
        } catch (error) {
            console.error('Error refreshing orders:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to refresh order list',
                life: 3000
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = async (filters: {
        startDate?: Date;
        endDate?: Date;
        orderID?: number;
        invoiceNo?: number;
        userID?: number;
        orderType?: number;
        pageNumber?: number;
        pageSize?: number;
        resetPagination?: boolean;
        infoMessage?: string;
    }) => {
        setLoading(true);
        setHasSearched(true);
        currentFilters.current = filters;

        if (filters.infoMessage) {
            setInfoMessage(filters.infoMessage);
        } else {
            setInfoMessage('');
        }

        const shouldResetPagination = filters.resetPagination;
        const currentPageNumber = shouldResetPagination ? 1 : filters.pageNumber || pageNumber;
        const currentPageSize = shouldResetPagination ? 10 : filters.pageSize || pageSize;
        try {
            const requestBody = {
                startDate: filters.startDate,
                endDate: filters.endDate,
                orderID: filters.orderID,
                invoiceNo: filters.invoiceNo,
                userID: filters.userID,
                orderType: filters.orderType,
                pageNumber: currentPageNumber,
                pageSize: currentPageSize
            };

            if (shouldResetPagination) {
                setPageNumber(1);
                setPageSize(10);
            }

            let response;
            if (filters.orderType && [3, 4, 5].includes(filters.orderType)) {
                response = await orderServices.orderInvoicedDetails(requestBody);
            } else {
                response = await orderServices.orderOrderdDetails(requestBody);
            }

            const sortedData = withPendingProductCounts([...(response.invoices || [])]).sort((a, b) => b.orderID - a.orderID);
            const enrichedData = await enrichOrderDisplayValues(sortedData);
            setOrderList(enrichedData);
            const validOrderIds = enrichedData.map(order => order.orderID).filter(id => id);
            setOrderId(validOrderIds);
            setTotalRecords(response.totalRecords || 0);
        } catch (error) {
            console.error('Error fetching orders:', error);
            setOrderList([]);
            setTotalRecords(0);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to fetch orders',
                life: 3000
            });
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setOrderList([]);
        setTotalRecords(0);
        setHasSearched(false);
        setOrderId([]);
        setInfoMessage('');
        setPageNumber(1);
        setPageSize(10);
        currentFilters.current = {};
        return {};
    };

    const handlePageChange = (event: DataTableStateEvent) => {
        const newPageNumber = Math.floor((event.first || 0) / (event.rows || 10)) + 1;
        const newPageSize = event.rows || 10;

        setPageNumber(newPageNumber);
        setPageSize(newPageSize);

        handleSearch({
            ...currentFilters.current,
            pageNumber: newPageNumber,
            pageSize: newPageSize,
            resetPagination: false
        });
    };

    const handleOrderExport = async () => {
        setExportLoading(prev => ({ ...prev, orders: true }));
        try {
            await orderExport(orderIDs);
        } catch (error) {
            console.error('Order export failed:', error);
        } finally {
            setExportLoading(prev => ({ ...prev, orders: false }));
        }
    };

    const handleDetailExport = async () => {
        setExportLoading(prev => ({ ...prev, orderDetail: true }));
        try {
            await detailExport(
                currentFilters.current.orderID,
                currentFilters.current.invoiceNo,
                currentFilters.current.userID,
                currentFilters.current.orderType,
                currentFilters.current.startDate,
                currentFilters.current.endDate
            );
        } catch (error) {
            console.error('Detail export failed:', error);
        } finally {
            setExportLoading(prev => ({ ...prev, orderDetail: false }));
        }
    };

    const handleInvoiceExport = async () => {
        setExportLoading(prev => ({ ...prev, invoice: true }));
        try {
            await invoiceExport(orderIDs);
        } catch (error) {
            console.error('Invoice export failed:', error);
        } finally {
            setExportLoading(prev => ({ ...prev, invoice: false }));
        }
    };

    const handleInvoiceDetailExport = async () => {
        setExportLoading(prev => ({ ...prev, invoiceDetail: true }));
        try {
            await invoiceDetailExport(orderIDs);
        } catch (error) {
            console.error('Invoice detail export failed:', error);
        } finally {
            setExportLoading(prev => ({ ...prev, invoiceDetail: false }));
        }
    };

    const readExcelFile = (file: File): Promise<any[]> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = (e) => {
                try {
                    const data = e.target?.result;
                    const workbook = XLSX.read(data, { type: 'array' });
                    const firstSheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[firstSheetName];
                    const jsonData = XLSX.utils.sheet_to_json(worksheet);
                    resolve(jsonData);
                } catch (error) {
                    reject(error);
                }
            };

            reader.onerror = (error) => reject(error);
            reader.readAsArrayBuffer(file);
        });
    };

    const handleExcelImport = async (event: any) => {
        const file = event.files[0];
        setImportLoading(true);
        try {
            const data = await readExcelFile(file);

            const uniqueOrders = new Map<string, {
                orderID: number;
                primaryContactNumber: string;
                sequenceNo: number;
                vehicleNo: string;
            }>();

            data.forEach((row: any) => {
                const orderID = row['Order#'];
                const contact = row['Driver Contact'];
                const sequence = row['Sequence No'];
                const vehicleNo = row["Vehicle No"];

                if (!orderID || !contact || !sequence || !vehicleNo) {
                    console.warn('Skipping row with missing required fields:', row);
                    return;
                }

                const parsedOrderID = Number(orderID);
                const parsedSequence = Number(sequence);
                const key = `${parsedOrderID}-${String(contact).trim()}`;

                if (!uniqueOrders.has(key)) {
                    uniqueOrders.set(key, {
                        orderID: parsedOrderID,
                        primaryContactNumber: String(contact).trim(),
                        sequenceNo: parsedSequence,
                        vehicleNo: String(vehicleNo).trim(),
                    });
                }
            });

            const extractedData = Array.from(uniqueOrders.values());

            if (extractedData.length === 0) {
                throw new Error('No valid data found in the file');
            }

            console.log('Cleaned deliveries:', extractedData);
            const response = await orderServices.importDriverFile(extractedData);

            toast.current?.show({
                severity: 'success',
                summary: 'Success',
                detail: response.message || `Successfully imported ${extractedData.length} unique orders`,
                life: 3000
            });

        } catch (error: any) {
            console.error('Error importing drivers list:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.message || error.message || 'Failed to import drivers list',
                life: 3000
            });
        } finally {
            fileUploadRef.current?.clear();
            setImportLoading(false);
        }
    };

    function handelPaymentOrderStatus() {
        setVisible(true);
    }

    const handelPullPaymentStatus = async (event: any) => {
        if (paymentOrderId !== '') {
            var response = await orderServices.getPaymentOrderStatus(Number(paymentOrderId))
            if (response) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'payment status pull successfully',
                    life: 3000
                });
                setTimeout(() => {
                    setVisible(false);
                }, 1000);
                setPaymentOrderId('');
                return;
            }
        }
        else {
            toast.current?.show({
                severity: 'info',
                summary: 'Info',
                detail: 'please enter Order #',
                life: 3000
            });
            return;
        }
    }
    return (
        <Suspense fallback={<AppLoading />}>
            <Toast ref={toast} />
            <div className="layout-content mt-3 mb-3 grid grid-table-mobile">
                <div className="col-12">
                    <div className="flex align-items-center justify-content-between flex-wrap gap-2 w-full card-container mb-0">
                        <h5 className="ml-1 mb-0">Order Detail</h5>
                        <div className="flex align-items-center gap-2">
                            <FileActionUpload menuId={9}
                                ref={fileUploadRef}
                                mode="basic"
                                name="excel-upload"
                                accept=".xlsx, .xls"
                                maxFileSize={5000000}
                                chooseLabel={importLoading ? 'Importing...' : 'Import Driver Orders List'}
                                auto
                                customUpload
                                uploadHandler={handleExcelImport}
                            />
                            {hasSearched && orderList.length > 0 && (
                                <span>
                                    <FileActionButton action="export"
                                        className="text-black border-round"
                                        onClick={handleDetailExport}
                                        icon={exportLoading.orderDetail ? "pi pi-spin pi-spinner mr-1" : "pi pi-file-export mr-1"}
                                        disabled={exportLoading.orderDetail}
                                        text
                                        raised
                                    >
                                        Export Order Detail
                                    </FileActionButton>
                                </span>
                            )}
                            <Button
                                severity="success"
                                onClick={handelPaymentOrderStatus}
                                text
                                raised
                                label="Pull Payment Status" />
                            <Dialog
                                visible={visible}
                                header={"Payment Status"}
                                onHide={() => { if (!visible) return; setVisible(false); }}
                            >
                                <>
                                    <Suspense fallback={<AppLoading />}>
                                        <Toast ref={toast} />
                                        <div className="col-12 card border-noround p-2">
                                            <div className="col-6">
                                                <div className="flex-auto">
                                                    <label htmlFor="number" className="font-bold block mb-2">
                                                        Order #
                                                    </label>
                                                    <InputText value={paymentOrderId} id="number" keyfilter="num" style={{ border: '1px solid #ced4da', borderRadius: '4px' }} onChange={(e) => setPaymentOrderId(e.target.value)} />
                                                </div>
                                            </div>
                                            <div className="col-12">
                                                <Button className='flex-end' onClick={handelPullPaymentStatus}>Submit</Button>
                                            </div>
                                        </div>
                                        {loading && <AppLoading />}
                                    </Suspense>
                                </>
                            </Dialog>
                        </div>
                    </div>
                </div>
                <OrderListSearch
                    onSearch={handleSearch}
                    setLoading={setLoading}
                    onReset={handleReset}
                />
                {loading ? (
                    <AppLoading />
                ) : hasSearched ? (
                    <OrderListDataTable
                        data={orderList}
                        pageNumber={pageNumber}
                        pageSize={pageSize}
                        totalRecords={totalRecords}
                        onPageChange={handlePageChange}
                        onOrderCancelled={refreshOrderList}
                        onOrderReturned={refreshOrderList}
                        onOrderAssigned={refreshOrderList}
                        infoMessage={infoMessage}
                    />
                ) : (
                    <div className="col-12 text-center p-4">
                        <p>Please apply filters to view orders</p>
                    </div>
                )}
            </div>
        </Suspense>
    );
};

export default OrderList;
